import React, { useContext, useEffect } from "react";

import { i18n } from "../../../translate/i18n";
import { ThemeContext } from "../../../context/Theme/ThemeContext";
import { SocialChannelBadge } from "./SocialChannelBadge";
import CommentFeedItem from "./CommentFeedItem";
import { resolveMediaUrl } from "../../../helpers/resolveMediaUrl";

export default function InstagramPostModal({
	open,
	group,
	onClose,
	onReply,
	onUpdateStatus,
	onLoadMoreComments,
	loadingMoreComments,
}) {
	const { darkMode } = useContext(ThemeContext);

	useEffect(() => {
		if (!open) return undefined;
		const onKey = (e) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		document.body.style.overflow = "hidden";
		return () => {
			document.removeEventListener("keydown", onKey);
			document.body.style.overflow = "";
		};
	}, [open, onClose]);

	if (!open || !group) return null;

	const { post, channel, comments = [] } = group;
	const total = group.commentsTotal ?? comments.length;
	const hasMoreComments = Boolean(group.hasMoreComments);
	const isLoadingMore = Boolean(loadingMoreComments);
	const mediaUrl = resolveMediaUrl(post?.thumbnailUrl);

	const panelBg = darkMode ? "#121212" : "#ffffff";
	const borderColor = darkMode ? "#262626" : "#dbdbdb";
	const textMain = darkMode ? "#f5f5f5" : "#262626";
	const textMuted = darkMode ? "#a8a8a8" : "#737373";
	const mediaBg = darkMode ? "#000000" : "#000000";

	return (
		<div
			className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6"
			style={{ background: "rgba(0,0,0,0.65)" }}
			onClick={onClose}
			role="dialog"
			aria-modal="true"
		>
			<button
				type="button"
				aria-label="Fechar"
				className="absolute right-4 top-4 z-10 inline-flex size-10 items-center justify-center rounded-full bg-black/40 text-white transition hover:bg-black/60"
				onClick={onClose}
			>
				<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
					<path d="M18 6L6 18M6 6l12 12" />
				</svg>
			</button>

			<div
				className="flex max-h-[92vh] w-full max-w-[1100px] overflow-hidden rounded-xl shadow-2xl"
				style={{ background: panelBg, border: `1px solid ${borderColor}` }}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Media — esquerda */}
				<div
					className="relative hidden min-h-[420px] w-[58%] items-center justify-center md:flex"
					style={{ background: mediaBg }}
				>
					{mediaUrl ? (
						<img
							src={mediaUrl}
							alt={post?.caption || "Publicação"}
							className="max-h-[92vh] max-w-full object-contain"
						/>
					) : (
						<div className="px-8 text-center text-sm text-white/60">
							Sem imagem da publicação
						</div>
					)}
				</div>

				{/* Conteúdo — direita */}
				<div className="flex w-full flex-col md:w-[42%]" style={{ color: textMain }}>
					{/* Header */}
					<div
						className="flex shrink-0 items-center justify-between gap-3 px-4 py-3"
						style={{ borderBottom: `1px solid ${borderColor}` }}
					>
						<div className="flex min-w-0 items-center gap-2">
							<SocialChannelBadge channel={channel} />
							<span className="truncate text-sm font-semibold">{channel}</span>
						</div>
						<span className="shrink-0 text-xs" style={{ color: textMuted }}>
							{i18n.t("socialComments.commentsCount", { count: total })}
						</span>
					</div>

					{/* Mobile media */}
					{mediaUrl && (
						<div className="md:hidden" style={{ background: mediaBg }}>
							<img
								src={mediaUrl}
								alt={post?.caption || "Publicação"}
								className="max-h-[40vh] w-full object-contain"
							/>
						</div>
					)}

					{/* Caption */}
					<div
						className="shrink-0 space-y-2 px-4 py-3"
						style={{ borderBottom: `1px solid ${borderColor}` }}
					>
						{post?.caption ? (
							<p className="m-0 whitespace-pre-wrap text-sm leading-relaxed">
								{post.caption}
							</p>
						) : (
							<p className="m-0 text-sm" style={{ color: textMuted }}>
								Sem descrição
							</p>
						)}
						{post?.permalink && post.permalink !== "#" && (
							<a
								href={post.permalink}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-block text-xs font-semibold text-primary hover:underline"
							>
								{i18n.t("socialComments.viewOriginal")}
							</a>
						)}
					</div>

					{/* Comments scroll */}
					<div className="min-h-0 flex-1 overflow-y-auto">
						{comments.length === 0 ? (
							<p className="px-4 py-10 text-center text-sm" style={{ color: textMuted }}>
								{i18n.t("socialComments.emptyComments")}
							</p>
						) : (
							comments.map((comment) => (
								<CommentFeedItem
									key={comment.id}
									comment={comment}
									onReply={onReply}
									onUpdateStatus={onUpdateStatus}
								/>
							))
						)}

						{hasMoreComments && (
							<div className="flex justify-center px-4 py-4">
								<button
									type="button"
									disabled={isLoadingMore}
									onClick={() => onLoadMoreComments?.(post.id)}
									className="btn btn-sm border disabled:opacity-60"
									style={{ borderColor, color: textMain }}
								>
									{isLoadingMore
										? i18n.t("socialComments.loadingMoreComments")
										: i18n.t("socialComments.loadMoreComments")}
								</button>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
